import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, createListCollection, Field, Heading, Input, ListCollection, Select, Show, SimpleGrid, Stack, Textarea} from "@chakra-ui/react"
import {useToast} from "@chakra-ui/toast";
import {CompanyExecutorFull, dataApi, ExecutorContract, ExecutorTask as ExTask} from "../../api/data-api";
import {DateTime} from "luxon";
import {Tooltip} from "../../components/Tooltip";
import {DocumentFile} from "../Document/documentFile";
import {useRecoilState} from "recoil";
import {companyData} from "../../components/hooks/recoilData";
import {equalArrays, errorNotify, errorNotifySilent} from "../../components/hooks/CommonUtils";
import {Checkbox} from "@chakra-ui/checkbox";
import {FileExt, FileListUploader} from "./fileListUploader"

export interface ExecutorTaskExt {
    id: number | null,
    name: string,
    type1: string,
    description: string,
    address: string,
    startDate: string,
    endDate: string,
    value: string,
    documentId: number | null,
    category: string,
    project: string,
    comments: string,
    calculationType: "TASK" | "HOUR",
    calculationTimeHEstimated: string,
    calculationTimeHActual: string,
    calculationTimeHVerified: string,
    resultValue: string,
    sended: boolean,
    executorContractIds: (number | null)[],
    companyExecutorIds: number[],
}

export const taskTypeList = createListCollection({
    items: [
        {label: "Local", value: "LOCAL"},
        {label: "Remote", value: "REMOTE"},
    ],
});

export const taskCalculationTypeList = createListCollection({
    items: [
        {label: "TASK", value: "TASK"},
        {label: "HOUR", value: "HOUR"},
    ],
});

export default function TaskItem(props: {
    task: ExTask | null,
    setTaskAndReturn: React.Dispatch<ExTask> // (task: ExTask) => void
}) {
    const [company] = useRecoilState(companyData);
    const [, setCompanyExecutors] = useState<CompanyExecutorFull[]>([]);
    const [companyExecutorList, setCompanyExecutorList] = useState<ListCollection>(createListCollection({items: []}));
    const [documentId, setDocumentId] = useState<number | null>(null);

    const inputDefault: ExecutorTaskExt = useMemo(() => {
        return {
            id: null,
            name: "",
            type1: "LOCAL",
            description: "",
            address: "",
            startDate: "",
            endDate: "",
            value: "100",
            documentId: null,
            category: "",
            project: "",
            comments: "",
            calculationType: "TASK",
            calculationTimeHEstimated: "1",
            calculationTimeHActual: "1",
            calculationTimeHVerified: "1",
            resultValue: "100",
            sended: false,
            executorContractIds: [],
            companyExecutorIds: [],
        }
    }, []);
    const [inputParams, setInputParams] = useState<ExecutorTaskExt>(inputDefault);
    const toast = useToast();
    const [files, setFiles] = useState<FileExt[]>([]);

    const prepareInput = useCallback((item: ExTask | null) => {
        const now = DateTime.now();
        let startDate = item ? item.START_DATE : now.toMillis();
        let endDate = item ? item.END_DATE : now.plus({day: 1}).set({hour: 23, minute: 59, second: 59}).toMillis();
        setInputParams({
            id: item ? item.ID : null,
            name: item ? item.NAME : "",
            type1: item && item.TYPE1 ? item.TYPE1 : "LOCAL",
            description: item ? item.DESCRIPTION : "",
            address: item && item.ADDRESS ? item.ADDRESS : "",
            startDate: DateTime.fromMillis(startDate).toFormat('HH:mm dd.MM.yyyy'),
            endDate: DateTime.fromMillis(endDate).toFormat('HH:mm dd.MM.yyyy'),
            value: item ? String(item.VALUE1) : "100",
            documentId: item ? item.DOCUMENT_ID : null,
            sended: !!(item && item.SENDED != null),
            category: item && item.CATEGORY ? item.CATEGORY : "",
            project: item && item.PROJECT ? item.PROJECT : "",
            comments: item && item.COMMENTS ? item.COMMENTS : "",
            resultValue: item && item.RESULT_VALUE ? String(item.RESULT_VALUE) : "",
            calculationType: item && item.CALCULATION_TYPE ? item.CALCULATION_TYPE : "TASK",
            calculationTimeHEstimated: item && item.CALCULATION_TIME_H_ESTIMATED ? String(item.CALCULATION_TIME_H_ESTIMATED) : "1",
            calculationTimeHActual: item && item.CALCULATION_TIME_H_ACTUAL ? String(item.CALCULATION_TIME_H_ACTUAL) : "1",
            calculationTimeHVerified: item && item.CALCULATION_TIME_H_VERIFIED ? String(item.CALCULATION_TIME_H_VERIFIED) : "1",
            executorContractIds: item && item.EXECUTOR_CONTRACT_ID ? [item.EXECUTOR_CONTRACT_ID] : [],
            companyExecutorIds: item && item.COMPANY_EXECUTOR_ID ? [item.COMPANY_EXECUTOR_ID] : [],
        });
        if (item && item.EXECUTOR_CONTRACT_ID && !item.COMPANY_EXECUTOR_ID) {
            dataApi.executorContractGet(item.EXECUTOR_CONTRACT_ID)
                .then((o) => setInputParams(prev => {
                    return {...prev, companyExecutorIds: [o.COMPANY_EXECUTOR_ID]};
                }))
                .catch((err) => errorNotifySilent(err));
        }
        setFiles([]);
    }, []);

    useEffect(() => {
        prepareInput(props.task);
        if (company) {
            dataApi.companyExecutorListFull(company.ID)
                .then((o) => {
                    setCompanyExecutors(o);
                    setCompanyExecutorList(createListCollection({
                        items: o.map(it => {
                            return {label: `${it.FIRST_NAME} ${it.SECOND_NAME} ${it.LAST_NAME} ${it.CODE_INN}`, value: String(it.ID)}
                        }),
                    }));
                })
                .catch((err) => errorNotify(err, toast));
        }
    }, [props, toast, company, prepareInput]);

    useEffect(() => {
        setDocumentId(props.task && props.task.DOCUMENT_ID ? props.task.DOCUMENT_ID : null);
    }, [props]);

    const setCompanyExecutorId = useCallback((companyExecutorIds: string[]) => {
        if (inputParams.id != null && companyExecutorIds.length > 1) {
            toast({title: "Multiple executors may set only in creation phase", status: "error"});
            return;
        }
        let ids = companyExecutorIds.map(s => parseInt(s[0]));
        if (equalArrays(inputParams.companyExecutorIds, ids))
            return;
        setInputParams((prevState) => {
            return {...prevState, companyExecutorIds: ids, executorContractId: []}
        });
        const promises: Promise<any>[] = [];
        ids.forEach(id => promises.push(dataApi.executorContractList(id)));
        Promise.all(promises)
            .then((value: ExecutorContract[][]) => {
                if (!value || value.length === 0)
                    return;
                setInputParams((prevState) => {
                    return {
                        ...prevState,
                        executorContractIds: value.map(v => {
                            if (!v || v.length === 0)
                                return null;
                            return v.sort((a, b) => b.CREATED - a.CREATED)[0].ID;
                        })
                    }
                });
            })
            .catch((err) => errorNotify(err, toast));

    }, [inputParams, toast]);

    const createOrUpdate = useCallback(() => {
        if (inputParams.executorContractIds.length === 0 || inputParams.companyExecutorIds.length === 0 || inputParams.executorContractIds.length > inputParams.companyExecutorIds.length) {
            toast({title: "Контракты не найдены", status: "error"});
            return;
        }
        for (let i = 0; i < inputParams.companyExecutorIds.length; i++) {
            if (inputParams.executorContractIds[i] === null) {
                toast({title: "Контракт не найден", status: "error"});
                return;
            }
        }
        for (let i = 0; i < inputParams.companyExecutorIds.length; i++) {
            const contractId = inputParams.executorContractIds[i];
            if (contractId === null)
                continue;
            dataApi.executorTaskCreateOrUpdate(
                inputParams.id, contractId, inputParams.companyExecutorIds[i], inputParams.type1, inputParams.name, inputParams.description, inputParams.address,
                DateTime.fromFormat(inputParams.startDate, 'HH:mm dd.MM.yyyy').toMillis(), DateTime.fromFormat(inputParams.endDate, 'HH:mm dd.MM.yyyy').toMillis(),
                parseFloat(inputParams.value), inputParams.category, inputParams.project, inputParams.comments, inputParams.calculationType, parseInt(inputParams.calculationTimeHEstimated))
                .then((value) => {
                    toast({title: "Save task", status: "success"});
                    if (files && value.DOCUMENT_ID) {
                        files.forEach(f => {
                            if (!f.file)
                                return;
                            dataApi.documentFileCreateOrUpdate(null, value.DOCUMENT_ID, f.name, f.file)
                                .then((v) => {
                                    toast({title: "Save file " + inputParams.name, status: "success"});
                                })
                                .catch((err) => errorNotify(err, toast));
                        });
                    }
                    props.setTaskAndReturn(value);
                    prepareInput(null);
                })
                .catch((err) => errorNotify(err, toast));
        }
    }, [props, inputParams, toast, prepareInput, files]);

    return <Box borderWidth={1} borderColor="gray">
        <Heading size="xl">Задача {inputParams.id}</Heading>
        <Box borderWidth={1} borderColor="gray">
            <Field.Root orientation="horizontal">
                <Field.Label>Название услуги <Field.RequiredIndicator/></Field.Label>
                <Input type="text" placeholder="Название"
                       value={inputParams.name}
                       onChange={(it) => setInputParams((prevState) => {
                           return {...prevState, name: it.target.value}
                       })}/>
            </Field.Root>
            <Field.Root orientation="horizontal">
                <Field.Label>Категория задания <Field.RequiredIndicator/></Field.Label>
                <Input type="text" placeholder="Категория"
                       value={inputParams.category}
                       onChange={(it) => setInputParams((prevState) => {
                           return {...prevState, category: it.target.value}
                       })}/>
            </Field.Root>
            <Field.Root orientation="horizontal">
                <Field.Label>Описание задачи <Field.RequiredIndicator/></Field.Label>
                <Textarea placeholder="Описание"
                          value={inputParams.description}
                          onChange={(it) => setInputParams((prev) => {
                              return {...prev, description: it.target.value}
                          })}/>
            </Field.Root>
            <Field.Root orientation="horizontal">
                <Field.Label>Стоимость единицы услуги\задания <Field.RequiredIndicator/></Field.Label>
                <Input type="number" placeholder="Сумма"
                       value={inputParams.value}
                       onChange={(it) => setInputParams((prevState) => {
                           return {...prevState, value: it.target.value}
                       })}/>
            </Field.Root>
            <SimpleGrid columns={[2]} columnGap={2} rowGap={2}>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Тип отплаты <Field.RequiredIndicator/></Field.Label>
                    <Select.Root
                        value={[inputParams.calculationType]}
                        // defaultValue={[String(inputParams.sex)]}
                        collection={taskCalculationTypeList}
                        onValueChange={(e: any) => setInputParams(prev => {
                            return {
                                ...prev, calculationType: e.value[0]
                            }
                        })}>
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder="Вид отплаты"/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                            <Select.Content>
                                {taskCalculationTypeList.items.map((it: any) => (
                                    <Select.Item item={it} key={it.value}>
                                        {it.label}
                                        <Select.ItemIndicator/>
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Positioner>
                    </Select.Root>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                        <Field.Label>Предполагаемое количество часов </Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Количество"
                           disabled={inputParams.calculationType === "TASK"}
                           value={inputParams.calculationTimeHEstimated}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, calculationTimeHEstimated: it.target.value}
                           })}/>
                </Field.Root>
            </SimpleGrid>
            <Show when={props.task && props.task.SENDED}>
                <SimpleGrid columns={[3]} columnGap={2} rowGap={2}>
                    <Field.Root orientation="horizontal">
                        <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                            <Field.Label>Указанное количество часов </Field.Label>
                        </Tooltip>
                        <Input type="text" placeholder="Количество"
                               disabled={true}
                               value={inputParams.calculationTimeHActual}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, calculationTimeHActual: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                            <Field.Label>Принятое количество часов </Field.Label>
                        </Tooltip>
                        <Input type="text" placeholder="Количество"
                               disabled={true}
                               value={inputParams.calculationTimeHVerified}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, calculationTimeHVerified: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Итоговая стоимость <Field.RequiredIndicator/></Field.Label>
                        <Input type="number" placeholder="Сумма"
                               disabled={true}
                               value={inputParams.resultValue !== "" ? inputParams.resultValue : inputParams.value}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, resultValue: it.target.value}
                               })}/>
                    </Field.Root>
                </SimpleGrid>
            </Show>
            <Box>Срок выполнения</Box>
            <SimpleGrid columns={[2]} columnGap={2} rowGap={2}>
                <Field.Root orientation="horizontal">
                    <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                        <Field.Label>Начало <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата начала"
                           value={inputParams.startDate}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, startDate: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                        <Field.Label>Окончание <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата окончания"
                           value={inputParams.endDate}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, endDate: it.target.value}
                           })}/>
                </Field.Root>
            </SimpleGrid>
            <SimpleGrid columns={[3]} columnGap={2} rowGap={2}>
                <Field.Root orientation="horizontal">
                    <Field.Label>Проект </Field.Label>
                    <Input type="text" placeholder="Проект"
                           value={inputParams.project}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, project: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label textStyle="sm">Место выполнения </Field.Label>
                    <Input type="text" placeholder="Адрес"
                           disabled={inputParams.type1 === "REMOTE"}
                           value={inputParams.address}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, address: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    {/*<CheckboxRoot*/}
                    {/*    // checked={inputParams.type1 !== "REMOTE"}*/}
                    {/*    onCheckedChange={(e: any) => setInputParams((prevState) => {*/}
                    {/*        return {...prevState, type1: e.checked ? "REMOTE" : "LOCAL"}*/}
                    {/*    })}*/}
                    {/*>*/}
                    {/*    /!*<CheckboxHiddenInput />*!/*/}
                    {/*    <CheckboxControl>*/}
                    {/*        <CheckboxIndicator />*/}
                    {/*    </CheckboxControl>*/}
                    {/*    <CheckboxLabel>Удаленно</CheckboxLabel>*/}
                    {/*</CheckboxRoot>*/}
                    <Checkbox
                        isChecked={inputParams.type1 === "REMOTE"}
                        onChange={(e) => {
                            // console.log("e=" + e.target.checked);
                            setInputParams((prevState) => {
                                return {...prevState, type1: e.target.checked ? "REMOTE" : "LOCAL"}
                            })
                        }}
                    >Удаленно</Checkbox>
                </Field.Root>
            </SimpleGrid>
            <Field.Root orientation="horizontal" required>
                <Field.Label>Исполнитель <Field.RequiredIndicator/></Field.Label>
                <Select.Root
                    value={inputParams.companyExecutorIds.map(id => String(id))}
                    // defaultValue={[String(inputParams.userId)]}
                    multiple
                    collection={companyExecutorList}
                    onValueChange={(e: any) => setCompanyExecutorId(e.value)}>
                    <Select.HiddenSelect/>
                    <Select.Control>
                        <Select.Trigger>
                            <Select.ValueText placeholder="Исполнитель"/>
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                            <Select.Indicator/>
                        </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                        <Select.Content>
                            {companyExecutorList.items.map((it: any) => (
                                <Select.Item item={it} key={it.value}>
                                    {it.label}
                                    <Select.ItemIndicator/>
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Positioner>
                </Select.Root>
            </Field.Root>
            {documentId ?
                (<DocumentFile documentId={documentId} editable={true}/>) :
                (<FileListUploader items={files} setItems={setFiles}/>)}
            <Field.Root orientation="horizontal">
                <Field.Label>Коментарий </Field.Label>
                <Textarea placeholder="Описание"
                          value={inputParams.comments}
                          onChange={(it) => setInputParams((prev) => {
                              return {...prev, comments: it.target.value}
                          })}/>
            </Field.Root>
            <Stack direction="row" h="10">
                <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                        disabled={inputParams.sended}
                        onClick={e => createOrUpdate()}>
                    {inputParams.id ? "Сохранить" : "Создать"}
                </Button>
            </Stack>
        </Box>
    </Box>
}